import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { fmtDate } from '../utils/datetime'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'

/**
 * Settings → SFax Accounts.
 *
 * WHY SFAX CREDENTIALS GET THEIR OWN PAGE WHILE EVERY OTHER PROVIDER'S LIVE ON THE JOB.
 * SFax binds fax callbacks to a whole account, or to an organization within one, and never to
 * a job — so there is no per-job callback URL to generate, several jobs sharing one account is
 * the expected shape, and duplicating four secrets across five jobs would guarantee they
 * drift. A job's SFax leg SELECTS an account here rather than restating its secrets (G9.4a).
 *
 * THE CALLBACK URLS ARE SHOWN PER ACCOUNT, AND NO PER-JOB URL IS OFFERED. Not a
 * simplification: SFax cannot bind one, and offering it would imply an isolation the provider
 * does not provide. The page says so, because an operator who saw only two URLs and several
 * jobs would otherwise reasonably wonder which job they belong to (G9.21).
 *
 * THE PORTAL PASSWORD IS NOT A FIELD HERE, ON PURPOSE. The API never uses it: the auth token
 * is minted from Username, API Key, Encryption Key and Init Vector alone. The credential file
 * carries a portal password alongside the API user, so its absence is stated rather than left
 * looking like a field someone forgot (G9.4c).
 *
 * SECRETS ARE WRITE-ONLY. The API returns has_* booleans and never a value; leaving a
 * credential box empty KEEPS what is stored. The placeholder says so, because the failure this
 * convention prevents is an operator saving an unrelated change and silently destroying a
 * stored credential.
 *
 * DELETE IS EXPECTED TO BE REFUSED while a job references the account, and the server's 409
 * names the jobs and offers disable instead. The page surfaces the reference list up front so
 * that refusal is predictable rather than a surprise on click.
 *
 * Server-side gating is the real control (`require_admin`, plus the /api/settings/ middleware
 * prefix). The `useIsAdmin` check here only decides what to render — a viewer who reached the
 * route would still be refused by the API.
 */

const API = '/api/settings/sfax-accounts'

interface JobReference {
  job_id: string
  job_name: string
  legs: string[]
  deleted: boolean
}

interface SfaxAccount {
  id: string
  display_name: string
  enabled: boolean
  has_username: boolean
  has_api_key: boolean
  has_encryption_key: boolean
  has_init_vector: boolean
  inbound_callback_url: string | null
  outbound_callback_url: string | null
  inbound_watermark_id: string | null
  outbound_watermark_id: string | null
  referenced_by: JobReference[]
  updated_at: string
  updated_by: string | null
}

interface CredentialForm {
  display_name: string
  username: string
  api_key: string
  encryption_key: string
  init_vector: string
}

const EMPTY_FORM: CredentialForm = {
  display_name: '',
  username: '',
  api_key: '',
  encryption_key: '',
  init_vector: '',
}

/** The four fields the SFax API needs — declared once so the form cannot drift from them. */
const CREDENTIAL_FIELDS: { key: keyof CredentialForm; label: string; hint: string }[] = [
  { key: 'username', label: 'API Username', hint: 'The API user, not the portal login' },
  { key: 'api_key', label: 'API Key', hint: 'From sfax.api.auth.nfo' },
  { key: 'encryption_key', label: 'Encryption Key', hint: 'AES key for the auth token' },
  { key: 'init_vector', label: 'Init Vector', hint: '16 characters' },
]

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => null)
  const detail = (body as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    return detail
      .map(d => (typeof d === 'string' ? d : (d as { msg?: string })?.msg ?? ''))
      .filter(Boolean)
      .join('; ') || fallback
  }
  return fallback
}

function CallbackUrl({ label, url }: { label: string; url: string | null }) {
  const [copied, setCopied] = useState(false)
  if (!url) {
    return (
      <div style={{ fontSize: '0.8rem', color: theme.textMuted }}>
        {label}: unavailable — PUBLIC_FQDN is not configured, so no URL can be generated.
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.75rem', color: theme.textMuted, minWidth: 68 }}>{label}</span>
      <code
        style={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          borderRadius: 3,
          padding: '0.2rem 0.4rem',
          fontSize: '0.75rem',
          wordBreak: 'break-all',
        }}
      >
        {url}
      </code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard?.writeText(url)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }}
        style={smallButton}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

export default function SfaxAccounts() {
  const isAdmin = useIsAdmin()
  const [accounts, setAccounts] = useState<SfaxAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState<CredentialForm>(EMPTY_FORM)
  const [editing, setEditing] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<CredentialForm>(EMPTY_FORM)
  const [busy, setBusy] = useState(false)

  const load = () => {
    setLoading(true)
    fetch(API)
      .then(async res => {
        if (!res.ok) throw new Error(await readError(res, 'Could not load SFax accounts'))
        return res.json() as Promise<SfaxAccount[]>
      })
      .then(setAccounts)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) {
        setError(await readError(res, 'Could not create the account'))
        return
      }
      setForm(EMPTY_FORM)
      setShowAdd(false)
      setNotice('Account created. Paste its callback URLs into SFax’s Fax Callback Settings.')
      load()
    } finally {
      setBusy(false)
    }
  }

  const handleSaveEdit = async (id: string) => {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      // Only non-empty values are sent. An empty box means KEEP, which is exactly what the
      // server does with a blank field — sending them anyway would rely on the server's
      // handling of a value the UI already knows means nothing.
      const payload: Record<string, string> = {}
      if (editForm.display_name) payload.display_name = editForm.display_name
      for (const { key } of CREDENTIAL_FIELDS) {
        if (editForm[key]) payload[key] = editForm[key]
      }
      const res = await fetch(`${API}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        setError(await readError(res, 'Could not update the account'))
        return
      }
      setEditing(null)
      setEditForm(EMPTY_FORM)
      load()
    } finally {
      setBusy(false)
    }
  }

  const handleToggle = async (account: SfaxAccount) => {
    setError(null)
    setNotice(null)
    const res = await fetch(`${API}/${account.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !account.enabled }),
    })
    if (!res.ok) setError(await readError(res, 'Could not change the account state'))
    load()
  }

  const handleDelete = async (account: SfaxAccount) => {
    setError(null)
    setNotice(null)
    if (account.referenced_by.length > 0) {
      // Refused server-side anyway; saying so here means the operator is not surprised by a
      // 409 and is pointed at the action they actually want.
      setError(
        `“${account.display_name}” is referenced by ${account.referenced_by.length} job(s): ` +
          `${account.referenced_by.map(r => r.job_name).join(', ')}. Disable it instead — that ` +
          `stops new sends while leaving its history readable.`,
      )
      return
    }
    if (!confirm(`Delete SFax account “${account.display_name}”? Its credentials are gone.`)) return
    const res = await fetch(`${API}/${account.id}`, { method: 'DELETE' })
    if (!res.ok) setError(await readError(res, 'Could not delete the account'))
    load()
  }

  if (!isAdmin) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>SFax Accounts</h2>
        <AdminOnly what="SFax account management" />
      </div>
    )
  }

  if (loading) return <div>Loading…</div>

  return (
    <div style={{ maxWidth: 860 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          marginBottom: '1.25rem',
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>SFax Accounts</h2>
          <p
            style={{
              margin: '0.35rem 0 0',
              color: theme.textMuted,
              fontSize: '0.85rem',
              lineHeight: 1.6,
              maxWidth: 640,
            }}
          >
            SFax binds fax callbacks to a whole account or organization, never to a job, so its
            credentials live here and a job’s SFax leg selects one of these records. Several
            jobs sharing one account is the expected shape.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} style={primaryButton}>
          + Add Account
        </button>
      </div>

      {error && <div style={errorBox}>{error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      {showAdd && (
        <form onSubmit={handleCreate} style={card}>
          <h3 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>New SFax Account</h3>
          <p style={{ margin: '0 0 1rem', fontSize: '0.78rem', color: theme.textMuted, lineHeight: 1.6 }}>
            All four fields are required: the auth token is minted from them and an account
            missing one would fail only at send time. The SFax <strong>portal password</strong>{' '}
            is deliberately not stored — the API never uses it, so it stays with you.
          </p>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle}>Display Name</label>
            <input
              value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              required
              style={inputStyle}
              placeholder="SFax Org — E2E"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {CREDENTIAL_FIELDS.map(({ key, label, hint }) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  required
                  type="password"
                  autoComplete="new-password"
                  style={inputStyle}
                />
                <div style={hintStyle}>{hint}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="submit" disabled={busy} style={primaryButton}>
              {busy ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAdd(false)
                setForm(EMPTY_FORM)
              }}
              style={secondaryButton}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {accounts.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: theme.textMuted }}>
          No SFax accounts configured. A job cannot use an SFax leg until one exists.
        </div>
      ) : (
        accounts.map(account => (
          <div key={account.id} style={card}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '1rem',
              }}
            >
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 600 }}>{account.display_name}</div>
                <div style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.2rem' }}>
                  <span style={{ color: account.enabled ? '#4caf50' : theme.textMuted }}>
                    {account.enabled ? '● Enabled' : '○ Disabled'}
                  </span>
                  {' · '}updated {fmtDate(account.updated_at)}
                  {account.updated_by ? ` by ${account.updated_by}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                <button onClick={() => handleToggle(account)} style={smallButton}>
                  {account.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => {
                    setEditing(editing === account.id ? null : account.id)
                    setEditForm(EMPTY_FORM)
                  }}
                  style={smallButton}
                >
                  {editing === account.id ? 'Close' : 'Edit'}
                </button>
                <button onClick={() => handleDelete(account)} style={dangerButton}>
                  Delete
                </button>
              </div>
            </div>

            <div style={{ marginTop: '0.9rem', display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              {CREDENTIAL_FIELDS.map(({ key, label }) => {
                const stored = account[`has_${key}` as keyof SfaxAccount] as boolean
                return (
                  <div key={key} style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                    {label}:{' '}
                    <strong style={{ color: stored ? theme.text : '#ef5350' }}>
                      {stored ? 'stored' : 'not set'}
                    </strong>
                  </div>
                )
              })}
            </div>

            <div
              style={{
                marginTop: '0.9rem',
                paddingTop: '0.75rem',
                borderTop: `1px solid ${theme.border}`,
              }}
            >
              <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem' }}>
                Callback URLs — paste these into SFax’s org-level{' '}
                <em>Fax Callback Settings</em>
              </div>
              <div style={{ display: 'grid', gap: '0.35rem' }}>
                <CallbackUrl label="Inbound" url={account.inbound_callback_url} />
                <CallbackUrl label="Outbound" url={account.outbound_callback_url} />
              </div>
              <p
                style={{
                  margin: '0.6rem 0 0',
                  fontSize: '0.74rem',
                  color: theme.textMuted,
                  lineHeight: 1.6,
                }}
              >
                These are per <strong>account</strong>, not per job. SFax cannot bind a callback
                to a job, so a per-job URL is not offered — it would imply an isolation the
                provider does not provide. Every job on this account shares these URLs, and each
                inbound fax is still matched to its own run by the E2E Run ID read off the page,
                never by arrival order or by the fax number it came in on.
              </p>
            </div>

            <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: theme.textMuted }}>
              {account.referenced_by.length === 0 ? (
                'Not referenced by any job.'
              ) : (
                <>
                  Used by:{' '}
                  {account.referenced_by.map((r, i) => (
                    <span key={r.job_id}>
                      {i > 0 && ', '}
                      <strong style={{ color: theme.text }}>{r.job_name}</strong> (
                      {r.legs.join(' + ')}
                      {r.deleted ? ', deleted' : ''})
                    </span>
                  ))}
                  {'. '}Deleting is refused while any job references it; disable it instead.
                </>
              )}
            </div>

            {editing === account.id && (
              <div
                style={{
                  marginTop: '0.9rem',
                  paddingTop: '0.75rem',
                  borderTop: `1px solid ${theme.border}`,
                }}
              >
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', color: theme.textMuted }}>
                  Leave a field <strong>empty to keep</strong> what is stored. Secrets are never
                  returned, so there is nothing to display — only to replace.
                </p>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={labelStyle}>Display Name</label>
                  <input
                    value={editForm.display_name}
                    onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))}
                    style={inputStyle}
                    placeholder={account.display_name}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {CREDENTIAL_FIELDS.map(({ key, label }) => {
                    const stored = account[`has_${key}` as keyof SfaxAccount] as boolean
                    return (
                      <div key={key}>
                        <label style={labelStyle}>{label}</label>
                        <input
                          value={editForm[key]}
                          onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                          type="password"
                          autoComplete="new-password"
                          style={inputStyle}
                          placeholder={stored ? '•••••••• (stored — leave blank to keep)' : 'not set'}
                        />
                      </div>
                    )
                  })}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.9rem' }}>
                  <button
                    onClick={() => void handleSaveEdit(account.id)}
                    disabled={busy}
                    style={primaryButton}
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(null)
                      setEditForm(EMPTY_FORM)
                    }}
                    style={secondaryButton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}

      <div
        style={{
          marginTop: '1.5rem',
          background: '#1a2a3a',
          border: '1px solid #24384a',
          borderRadius: 4,
          padding: '1rem',
          fontSize: '0.82rem',
          lineHeight: 1.65,
        }}
      >
        <strong>Why the portal password is not here.</strong> SFax’s auth token is built from
        the API Username, API Key, Encryption Key and Init Vector, and nothing else. The portal
        password is never sent to the API, so this application does not store it — holding a
        credential it has no use for would be pure liability. It stays with you.
      </div>
    </div>
  )
}

const card: React.CSSProperties = {
  background: theme.bgAlt,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: '1.1rem',
  marginBottom: '1rem',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.78rem',
  color: theme.textMuted,
  marginBottom: '0.2rem',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  color: theme.textMuted,
  marginTop: '0.2rem',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem',
  background: theme.bg,
  color: theme.text,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  fontSize: '0.9rem',
  boxSizing: 'border-box',
}

const primaryButton: React.CSSProperties = {
  padding: '0.5rem 1rem',
  background: theme.primary,
  color: theme.onPrimary,
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
}

const secondaryButton: React.CSSProperties = {
  padding: '0.5rem 1rem',
  border: `1px solid #555`,
  background: theme.bgAlt,
  color: theme.text,
  borderRadius: 4,
  cursor: 'pointer',
}

const smallButton: React.CSSProperties = {
  padding: '0.25rem 0.55rem',
  border: `1px solid #555`,
  background: theme.bgAlt,
  color: theme.text,
  borderRadius: 3,
  cursor: 'pointer',
  fontSize: '0.78rem',
}

const dangerButton: React.CSSProperties = {
  ...smallButton,
  border: '1px solid #ef5350',
  color: '#ef5350',
}

const errorBox: React.CSSProperties = {
  background: '#3a1e1e',
  border: '1px solid #5a2a2a',
  color: '#ef9a9a',
  padding: '0.75rem',
  borderRadius: 4,
  marginBottom: '1rem',
  fontSize: '0.85rem',
  lineHeight: 1.6,
}

const noticeBox: React.CSSProperties = {
  background: '#1e3a24',
  border: '1px solid #2a5a33',
  color: '#a5d6a7',
  padding: '0.75rem',
  borderRadius: 4,
  marginBottom: '1rem',
  fontSize: '0.85rem',
}
